import React from "react";
import { useAnimateOnScroll } from "./useAnimateOnScroll";

type SplitTextProps = {
  text: string;
  as?: keyof JSX.IntrinsicElements;
  /** Base delay in seconds between characters */
  charDelay?: number;
  /** Animation duration for each character (e.g. '0.5s') */
  duration?: string;
  /** animate.css class to use, defaults to fadeInUp */
  animationClass?: string;
  className?: string;
};

/**
 * Detects if text contains Arabic script characters (Arabic, Farsi, Urdu, Pashto, etc.)
 */
function containsArabicScript(text: string): boolean {
  // Arabic Unicode ranges: 0600-06FF (Arabic), 0750-077F (Arabic Supplement), 
  // 08A0-08FF (Arabic Extended-A), FB50-FDFF (Arabic Presentation Forms-A), 
  // FE70-FEFF (Arabic Presentation Forms-B)
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  return arabicRegex.test(text);
}

/**
 * Renders text split into characters, with a slight stagger between each character.
 * Uses the existing animate-on-scroll hook and animate.css classes already in the app.
 * For Arabic/RTL text, renders as whole words to preserve letter connections.
 */
export function SplitText({
  text,
  as = "span",
  charDelay = 0.03,
  duration = "0.5s",
  animationClass = "animate__fadeInUp",
  className = "",
}: SplitTextProps) {
  useAnimateOnScroll();

  const Wrapper = as as any;
  const hasArabic = containsArabicScript(text);
  
  // For Arabic/RTL text, don't split character by character - render as whole words
  if (hasArabic) {
    const tokens = text.split(/(\s+)/);
    let wordCounter = 0;
    
    return (
      <Wrapper className={`split-text ${className}`}>
        {tokens.map((token, tokenIdx) => {
          const isSpace = /^\s+$/.test(token);
          if (isSpace) {
            return <span key={`space-${tokenIdx}`} aria-hidden="true">{" "}</span>;
          }
          // Render whole word without character splitting for Arabic
          const delay = (wordCounter * charDelay * 3).toFixed(2) + "s"; // Slower animation for words
          const idx = wordCounter++;
          return (
            <span
              key={`word-${tokenIdx}`}
              className="split-word fade-up-0"
              data-animation={animationClass}
              data-duration={duration}
              data-delay={delay}
            >
              {token}
            </span>
          );
        })}
        <span className="sr-only">{text}</span>
      </Wrapper>
    );
  }

  // Original character-by-character splitting for non-Arabic text
  const tokens = text.split(/(\s+)/);
  let charCounter = 0;

  return (
    <Wrapper className={`split-text ${className}`}>
      {tokens.map((token, tokenIdx) => {
        const isSpace = /^\s+$/.test(token);
        if (isSpace) {
          // Render a regular space so the browser can collapse it at line starts
          return <span key={`space-${tokenIdx}`} aria-hidden="true">{" "}</span>;
        }
        // Render a word as a nowrap group to avoid breaking in the middle of words
        const letters = Array.from(token);
        return (
          <span key={`word-${tokenIdx}`} className="split-word">
            {letters.map((char) => {
              const delay = (charCounter * charDelay).toFixed(2) + "s";
              const idx = charCounter++;
              return (
                <span
                  key={`c-${tokenIdx}-${idx}`}
                  aria-hidden="true"
                  className="split-char fade-up-0"
                  data-animation={animationClass}
                  data-duration={duration}
                  data-delay={delay}
                >
                  {char}
                </span>
              );
            })}
          </span>
        );
      })}
      {/* Visually hidden full text for screen readers to avoid choppy char-by-char reading */}
      <span className="sr-only">{text}</span>
    </Wrapper>
  );
}


